import { useEffect } from 'react';
/**
 * Attaches an IntersectionObserver to all [data-reveal] elements inside
 * `containerRef` (or document.body if null). Adds [data-visible] when the
 * element enters the viewport, triggering the CSS transition defined in
 * index.css. Cleans up observer on unmount.
 *
 * Usage:
 *   const ref = useRef<HTMLDivElement>(null);
 *   useScrollReveal(ref);
 *   // In JSX: <div data-reveal>content</div>
 */
export function useScrollReveal(containerRef) {
    useEffect(() => {
        if (typeof window === 'undefined' || !('IntersectionObserver' in window)) {
            document.querySelectorAll('[data-reveal]').forEach((el) => {
                el.setAttribute('data-visible', '');
            });
            return;
        }
        const root = containerRef.current ?? document.body;
        const targets = root.querySelectorAll('[data-reveal]');
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    entry.target.setAttribute('data-visible', '');
                    observer.unobserve(entry.target);
                }
            });
        }, { threshold: 0.12, rootMargin: '-40px 0px' });
        targets.forEach((el) => observer.observe(el));
        return () => observer.disconnect();
    }, [containerRef]);
}
